/**
 * EditTextModal — the body of the Description / Tone / Writing Style modals.
 *
 * These are the attribute editors on a structure template: one long text field
 * and three buttons. The chrome around them — the glass panel, the icon, the
 * headline — belongs to `GlobalModal`; this component is only what sits inside
 * it.
 *
 * It used to paint its own `bg-white dark:bg-gray-800` over that panel, which
 * is how a glass dialog ended up with a flat white slab in the middle of it.
 * A modal body has no surface of its own: the panel underneath is the surface,
 * and the only thing drawn here is the field.
 *
 * The width used to be `w-[400px]`, or `w-[600px]` past `md` — two fixed
 * numbers, so on a narrow window the dialog was wider than the window. It is a
 * width the content asks for and a ceiling the viewport sets.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';

interface EditTextModalProps {
  /** Drawn by the dialog's own header — kept so callers need not change. */
  title?: string;
  initialValue: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
  rows?: number;
  placeholder?: string;
}

const EditTextModal: React.FC<EditTextModalProps> = ({
  initialValue,
  onConfirm,
  onCancel,
  rows = 6,
  placeholder = '',
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);

  return (
    <div className="w-[min(36rem,80vw)] space-y-4">
      <div className="px-4">
        <Textarea
          id=""
          label=""
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          h="h-[200px]"
        />
      </div>

      {/* Hairline, like every other divider in the app — this was a
          `border-gray-300` that stayed light grey in dark mode. */}
      <div className="flex justify-end gap-2 border-t border-hairline px-4 pt-4">
        <Button variant="neutral" size="small" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button variant="outline" size="small" onClick={() => setValue('')}>
          {t('Clear')}
        </Button>
        <Button variant="primary" size="small" onClick={() => onConfirm(value)}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default EditTextModal;
